interface ReactNativeWebViewBridge {
  postMessage: (msg: string) => void;
}

interface BridgeSigningResponse {
  sigma2: string;
  modulus: string;
  publicExponent: number;
}

interface BridgeSigningRejection {
  code?: string;
  message?: string;
}

interface BridgeSigningResult {
  sigma2Bytes: Uint8Array;
  modulus: string;
  publicExponent: number;
}

type BridgeResolveHandler = (response: BridgeSigningResponse) => void;
type BridgeRejectHandler = (rejection: BridgeSigningRejection) => void;

export type WindowWithBridge = Window & {
  ReactNativeWebView?: ReactNativeWebViewBridge;
  __sskBridgeReady?: boolean;
  __sskBridgeResolve?: BridgeResolveHandler;
  __sskBridgeReject?: BridgeRejectHandler;
  __sskBridgePending?: boolean;
};

const BRIDGE_UNAVAILABLE_MESSAGE =
  'React Native WebView bridge is not available.';
const BRIDGE_BUSY_MESSAGE = 'Bridge signing already in flight.';
const BRIDGE_INVALID_RESPONSE_MESSAGE =
  'The mobile authenticator returned an invalid signing response.';
const BRIDGE_TIMEOUT_MS = 30_000;

// __sskBridgeReady is set by the device-auth host before the WebView loads web-login;
// without it we assume a generic WebView that doesn't implement the signAssertion contract.
export function canUseSskBridge(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const win = window as WindowWithBridge;

  return (
    typeof win.ReactNativeWebView !== 'undefined' &&
    win.__sskBridgeReady === true
  );
}

export function postBridgeSignAssertion(signedInfoHashHex: string): void {
  const bridge =
    typeof window !== 'undefined'
      ? (window as WindowWithBridge).ReactNativeWebView
      : undefined;

  if (!bridge?.postMessage) {
    throw new Error(BRIDGE_UNAVAILABLE_MESSAGE);
  }

  bridge.postMessage(
    JSON.stringify({
      action: 'signAssertion',
      signedInfoHash: signedInfoHashHex,
    })
  );
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function clearPending(win: WindowWithBridge): void {
  win.__sskBridgePending = false;
  win.__sskBridgeResolve = undefined;
  win.__sskBridgeReject = undefined;
}

export function requestBridgeSigning(
  signedInfoHashHex: string
): Promise<BridgeSigningResult> {
  return new Promise<BridgeSigningResult>((resolve, reject) => {
    const win = window as WindowWithBridge;

    if (win.__sskBridgePending) {
      reject(new Error(BRIDGE_BUSY_MESSAGE));

      return;
    }

    win.__sskBridgePending = true;

    const timeoutHandle = window.setTimeout(() => {
      clearPending(win);
      reject(
        new Error(
          'The mobile authenticator did not respond in time. Please try again.'
        )
      );
    }, BRIDGE_TIMEOUT_MS);

    win.__sskBridgeResolve = (response: BridgeSigningResponse) => {
      window.clearTimeout(timeoutHandle);
      clearPending(win);

      if (
        !response ||
        typeof response.sigma2 !== 'string' ||
        typeof response.modulus !== 'string' ||
        typeof response.publicExponent !== 'number'
      ) {
        reject(new Error(BRIDGE_INVALID_RESPONSE_MESSAGE));

        return;
      }

      let sigma2Bytes: Uint8Array;

      try {
        sigma2Bytes = base64ToBytes(response.sigma2);
      } catch {
        reject(new Error(BRIDGE_INVALID_RESPONSE_MESSAGE));

        return;
      }

      resolve({
        sigma2Bytes,
        modulus: response.modulus,
        publicExponent: response.publicExponent,
      });
    };

    win.__sskBridgeReject = (rejection: BridgeSigningRejection) => {
      window.clearTimeout(timeoutHandle);
      clearPending(win);

      if (!rejection) {
        reject(new Error(BRIDGE_UNAVAILABLE_MESSAGE));
      }

      const message =
        rejection.message ?? 'The mobile authenticator rejected signing.';
      const codeSuffix = rejection.code ? ` (${rejection.code})` : '';
      reject(new Error(`${message}${codeSuffix}`));
    };

    try {
      postBridgeSignAssertion(signedInfoHashHex);
    } catch (error) {
      window.clearTimeout(timeoutHandle);
      clearPending(win);
      reject(error);
    }
  });
}
